import React, { FormEvent, Component } from 'react';
import { Button, Col, Form, FormControlProps, Row, Spinner, ToggleButton, ToggleButtonGroup, Alert, Card } from 'react-bootstrap';
import { CallbackArgument, Focused } from 'react-credit-cards';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import DatePicker from 'react-modern-calendar-datepicker';


import { formatCreditCardNumber, formatCVC, formatExpirationDate, numberFormatter, datetoObject, formatDrewDate } from '../../utils/utils';
import * as backendAPI from '../../api/backendAPI';
import BasicInfoFields from './BasicInfoFields';
import CreditCardFields from './CreditCardFields';
import ACHFields from './ACHFields';
import { Redirect } from 'react-router-dom';
import styles from './MasterPaymentForm.module.scss';

import { Company } from '../../models/Company';
import { Invoice } from '../../models/Invoice';
import { US_STATES, STATES_WITHOUT_CC_SURCHARGE } from '../../utils/constants';
import PaymentTypeValidator from './PaymentTypeValidator';
import { PaymentValidationResult, InvoicePaymentMap } from '../../utils/paymentTypeUtils';



interface PaymentFormProps {
  policyNumber: string;
  impersonationToken: string;
  companyName?: string;
  payee?: string;
  amount?: number;
  isPaymentPlan?: boolean;
  paymentComment?: string;
  selectedInvoice?: Invoice[] | Invoice | null;
  selectedPolicy?: string,
  company?: Company;
  dueDate: string;

}

export interface CreditCardInformation {
  accountHolder: string;
  cardNumber: string;
  cvc: string;
  expiry: string;
  postalCode?: string;
}

type AccountType = 'PersonalChecking' | 'PersonalSavings' | 'CorporateChecking' | 'CorporateSavings';

export interface BankAccountInformation {
  accountHolder?: string;
  firstName?: string;
  lastName?: string;
  accountType: AccountType;
  routingNumber?: string;
  accountNumber?: string;
  confirmAccountNumber?: string;
}

interface PaymentFormState {
  creditCardInformation: CreditCardInformation;
  bankAccountInformation: BankAccountInformation;
  amount?: number;
  payer: string;
  insuredName?: string;
  payerFee: number;
  issuer: string;
  focused?: Focused;
  emailAddress: string;
  paymentMethod: string;
  total: number;
  redirect?: any;
  errorMessage?: string;
  submitting: boolean;
  tosAccepted: boolean;
  scheduleAccepted: boolean;
  isPaymentPlan?: boolean;
  paymentComment?: string;
  scheduledDate?: any;
  isScheduled: boolean;
  // invoiceID?: number | null;
  selectedInvoice?: Invoice | null;
  companyName?: string;
  bankName?: string;
  payorState: string;
  showCCAlert: boolean;
  paymentValidationResult?: PaymentValidationResult;
  invoicePayments: InvoicePaymentMap;
  validationLoading: boolean;
}

export const FEES = {
  CC_FEE: .0375,
  ACH_FEE: 3
};





class MasterPaymentForm extends Component<PaymentFormProps, PaymentFormState> {
  private validationRequestId = 0;
  state: PaymentFormState = {
    submitting: false,
    tosAccepted: false,
    scheduleAccepted: false,
    scheduledDate: datetoObject(new Date(Date.now() + 86400000).toString()),
    isScheduled: false,
    creditCardInformation: {
      accountHolder: "",
      cardNumber: "",
      cvc: "123",
      expiry: "",
      postalCode: "",
    },
    bankAccountInformation: {
      accountHolder: "",
      firstName: "",
      lastName: "",
      accountType: "PersonalChecking",
      routingNumber: "",
      accountNumber: "",
    },
    payerFee: 0,
    issuer: "",
    emailAddress: "",
    payer: "",
    paymentMethod: "ach",
    total: 0,
    isPaymentPlan: false,
    paymentComment: '',
    // invoiceID: null,
    bankName: '',
    payorState: '',
    showCCAlert: false,
    paymentValidationResult: undefined,
    invoicePayments: {},
    validationLoading: false,
  };


  async componentDidMount() {

    this.setState({
      amount: this.props.amount,
      payer: this.props.payee ? this.props.payee : '',
      paymentComment: this.props.paymentComment ? this.props.paymentComment : '',
    }, () => {
      // console.table(this.state);
      this.calculatePaymentDetails();
      this.initializeInvoicePayments();

    });
  }


  async componentDidUpdate(
    prevProps: Readonly<PaymentFormProps>,
    prevState: Readonly<PaymentFormState>,
    snapshot?: any
  ) {
    if (prevProps.amount !== this.props.amount && this.props.amount) {
      this.setState({ amount: this.props.amount }, () => this.calculatePaymentDetails());
    }

    if (prevProps.selectedInvoice !== this.props.selectedInvoice) {
      this.initializeInvoicePayments();
    }
  }

  getSelectedInvoices = (): Invoice[] => {
    const { selectedInvoice } = this.props;
    if (!selectedInvoice) {
      return [];
    }

    if (Array.isArray(selectedInvoice)) {
      return selectedInvoice as Invoice[];
    }

    return [selectedInvoice as Invoice];
  };

  initializeInvoicePayments = () => {
    const invoices = this.getSelectedInvoices();
    if (!invoices.length) {
      return;
    }

    const invoicePayments: InvoicePaymentMap = {};
    invoices.forEach((invoice) => {
      const amount = parseFloat(invoice.amount);
      invoicePayments[invoice.InvoiceNumber] = isNaN(amount) ? 0 : amount;
    });

    this.setState({ invoicePayments }, () => {
      this.runValidation();
    });
  };

  getScheduledDateString = (): string | undefined => {
    if (!this.state.scheduledDate) {
      return undefined;
    }

    const { year, month, day } = this.state.scheduledDate;
    if (!year || !month || !day) {
      return undefined;
    }

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  runValidation = async () => {
    const invoices = this.getSelectedInvoices();
    if (!invoices.length) {
      return;
    }

    const invoicePaymentsPayload = invoices.map((invoice) => ({
      invoiceNumber: invoice.InvoiceNumber,
      amount: this.state.invoicePayments[invoice.InvoiceNumber],
    }));

    const payload = {
      policyNumber: this.props.policyNumber,
      invoiceNumbers: invoices.map((invoice) => invoice.InvoiceNumber),
      invoicePayments: invoicePaymentsPayload,
      isScheduled: this.state.isScheduled,
      scheduledDate: this.state.isScheduled ? this.getScheduledDateString() : undefined,
    };

    const currentRequest = ++this.validationRequestId;
    this.setState({ validationLoading: true });

    try {
      const result: PaymentValidationResult = await backendAPI.validatePaymentSelection(payload);

      if (currentRequest !== this.validationRequestId) {
        return;
      }

      const sanitizedPayments: InvoicePaymentMap = {};
      (result.invoices || []).forEach((invoice) => {
        sanitizedPayments[invoice.invoiceNumber] = invoice.requestedAmount;
      });

      this.setState({
        invoicePayments: Object.keys(sanitizedPayments).length ? sanitizedPayments : this.state.invoicePayments,
        paymentValidationResult: result,
        validationLoading: false,
        amount: result.totalAmount ?? this.state.amount,
        errorMessage: result.isValid ? undefined : (result.errors && result.errors.length ? result.errors[0] : result.message),
      }, () => {
        this.calculatePaymentDetails();
        this.enforceScheduleAvailability(result);
      });
    } catch (error) {
      if (currentRequest !== this.validationRequestId) {
        return;
      }

      const responseResult: PaymentValidationResult | undefined = error?.response?.data;

      if (responseResult) {
        const sanitizedPayments: InvoicePaymentMap = { ...this.state.invoicePayments };
        (responseResult.invoices || []).forEach((invoice) => {
          sanitizedPayments[invoice.invoiceNumber] = invoice.requestedAmount;
        });

        this.setState({
          invoicePayments: sanitizedPayments,
          paymentValidationResult: responseResult,
          validationLoading: false,
          amount: responseResult.totalAmount ?? this.state.amount,
          errorMessage: responseResult.errors && responseResult.errors.length
            ? responseResult.errors[0]
            : responseResult.message ?? 'Validation failed.',
        }, () => {
          this.calculatePaymentDetails();
          this.enforceScheduleAvailability(responseResult);
        });
      } else {
        console.error('Validation error', error);
        this.setState({
          validationLoading: false,
          errorMessage: 'Failed to validate payment selection. Please try again.',
        });
      }
    }
  };

  enforceScheduleAvailability = (result?: PaymentValidationResult) => {
    const schedulingAvailable = this.isSchedulingAvailable(result);
    if (!schedulingAvailable && (this.state.scheduleAccepted || this.state.isScheduled)) {
      this.setState({ scheduleAccepted: false, isScheduled: false });
    }
  };

  isSchedulingAvailable = (result?: PaymentValidationResult): boolean => {
    if (!result) {
      return false;
    }

    if (result.type === 'legacy') {
      return false;
    }

    if (result.code === 'SCHEDULING_NOT_ALLOWED' || result.code === 'SCHEDULE_PAST_DUE') {
      return false;
    }

    const invoices = result.invoices || [];
    if (!invoices.length) {
      return false;
    }

    if (invoices.some((invoice) => invoice.isPastDue)) {
      return false;
    }

    return Boolean(result.scheduleWindow?.max);
  };

  handleInvoiceAmountChange = (invoiceNumber: string, amount: number, shouldValidate: boolean = false) => {
    this.setState((prevState) => ({
      invoicePayments: {
        ...prevState.invoicePayments,
        [invoiceNumber]: amount,
      },
    }), () => {
      if (shouldValidate) {
        this.runValidation();
      }
    });
  };

  handleCallback = ({ issuer }: CallbackArgument, isValid: boolean) => {
    if (isValid) {
      this.setState({ issuer });
    }
  };

  handleInputFocus = ({ target }: any) => {
    this.setState({
      focused: target.name,
    });
  };

  handleInputChange = (e: FormEvent<FormControlProps>) => {
    const target = e.target as HTMLInputElement;

    this.handleBasicInfoInputChange(target);

    if (this.state.paymentMethod === "cc") {
      this.handleCreditCardInputChange(target);
    } else if (this.state.paymentMethod === "ach") {
      this.handleAchInputChange(target);
    }
  };

  handleBasicInfoInputChange = (target: HTMLInputElement) => {
    if (target.name === "payerName") {
      this.setState({ payer: target.value });
    } else if (target.name === "insuredName") {
      this.setState({ insuredName: target.value });
    } else if (target.name === "payerEmail") {
      this.setState({ emailAddress: target.value });
    } else if (target.name === "paymentComment") {
      this.setState({ paymentComment: target.value });
    } else if (target.name === "isPaymentPlan") {
      let val = target.value === "1" ? true : false;
      this.setState({ isPaymentPlan: val });
    } else if (target.name === "payorState") {
      this.setState({ payorState: target.value }, () => {
        if (STATES_WITHOUT_CC_SURCHARGE.includes(this.state.payorState)) {
          this.setState({ paymentMethod: 'ach' });
        }
      });
    } else if (target.name === "amount") {
      const numeric = parseFloat((target.value || '').replace(/[^0-9.]/g, ''));
      this.setState({ amount: isNaN(numeric) ? 0 : numeric }, () => this.calculatePaymentDetails());
    }
  };

  calculatePaymentDetails = () => {
    const baseAmount = this.state.amount ?? 0;
    const fee =
      this.state.paymentMethod === "cc"
        ? baseAmount * FEES.CC_FEE
        : baseAmount > 0
          ? FEES.ACH_FEE
          : 0;
    const total = baseAmount + fee;
    this.setState({
      payerFee: fee ? fee : 0,
      total: total ? total : 0,
    });
  };

  handleCreditCardInputChange = (target: HTMLInputElement) => {
    if (target.name === "number") {
      target.value = formatCreditCardNumber(target.value);
      this.setState({
        creditCardInformation: {
          ...this.state.creditCardInformation,
          cardNumber: target.value,
        },
      });
    } else if (target.name === "expiry") {
      target.value = formatExpirationDate(target.value);
      this.setState({
        creditCardInformation: {
          ...this.state.creditCardInformation,
          expiry: target.value,
        },
      });
    } else if (target.name === "cvc") {
      target.value = formatCVC(target.value);
      this.setState({
        creditCardInformation: {
          ...this.state.creditCardInformation,
          cvc: target.value,
        },
      });
    } else if (target.name === "name") {
      this.setState({
        creditCardInformation: {
          ...this.state.creditCardInformation,
          accountHolder: target.value,
        },
      });
    }
  };

  handleAchInputChange = (target: HTMLInputElement) => {
    if (this.state.bankAccountInformation.accountType === "PersonalChecking") {
      if (target.name === "firstName") {
        this.setState({
          bankAccountInformation: {
            ...this.state.bankAccountInformation,
            firstName: target.value,
          },
        });
      } else if (target.name === "lastName") {
        this.setState({
          bankAccountInformation: {
            ...this.state.bankAccountInformation,
            lastName: target.value,
          },
        });
      }
    }

    if (this.state.bankAccountInformation.accountType === "CorporateChecking") {
      if (target.name === "accountHolder") {
        this.setState({
          bankAccountInformation: {
            ...this.state.bankAccountInformation,
            accountHolder: target.value,
          },
        });
      }
    }

    if (target.name === "accountNumber") {
      this.setState({
        bankAccountInformation: {
          ...this.state.bankAccountInformation,
          accountNumber: target.value,
        },
      });
    } else if (target.name === "routingNumber") {


      console.log(target.value);
      if (target.value.length === 9) {
        backendAPI.getBankName(target.value).then(res => {
          console.log(res);
          this.setState({
            bankName: res.name.replace('Na', '').replace(',', '').replace('N.a.', ''),
          });

        }).catch(err => {
          console.log(err);
        });

      }
      else {
        this.setState({
          bankName: '',
        });
      }

      this.setState({
        bankAccountInformation: {
          ...this.state.bankAccountInformation,
          routingNumber: target.value,
        },
      });
    } else if (target.name === "confirmAccountNumber") {
      this.setState({
        bankAccountInformation: {
          ...this.state.bankAccountInformation,
          confirmAccountNumber: target.value,
        },
      });
    }
  };

  togglePaymentMethod = async (value: string) => {
    if (STATES_WITHOUT_CC_SURCHARGE.includes(this.state.payorState) && value === 'cc') {
      return; // Prevent switching to credit card for restricted states
    }
    await this.setState({
      paymentMethod: value,
    });
    await this.calculatePaymentDetails();
  };

  toggleAchType = () => {
    if (this.state.bankAccountInformation.accountType === "CorporateChecking") {
      this.setState({
        bankAccountInformation: {
          accountType: "PersonalChecking",
        },
      });
    } else {
      this.setState({
        bankAccountInformation: {
          accountType: "CorporateChecking",
        },
      });
    }
  };

  async sendPayment(request: PaymentFormState) {
    if (this.state.submitting) {
      return;
    }

    // Validation is only required when the user has actually selected invoices.
    // The "New Policy?" / free-form flow has no invoices -> no validation needed.
    const hasSelectedInvoices = this.getSelectedInvoices().length > 0;
    if (hasSelectedInvoices && (!this.state.paymentValidationResult || !this.state.paymentValidationResult.isValid)) {
      this.setState({
        errorMessage: 'Please resolve validation errors before submitting.',
      });
      return;
    }

    // Insured Name is required on the free-form / "new policy" path so
    // accounting has something to apply the deposit to in ePay.
    if (!this.props.policyNumber && !(this.state.insuredName ?? '').trim()) {
      this.setState({
        errorMessage: 'Insured Name is required.',
      });
      return;
    }

    this.setState({ submitting: true, errorMessage: undefined });
    let monthYear: string[] = request.creditCardInformation.expiry.split("/");
    if (monthYear.length !== 2) {
      monthYear = ["0", "0"];
    }
    let tokenRequest;
    if (this.state.paymentMethod === "cc") {
      tokenRequest = {
        payer: request.payer,
        emailAddress: request.emailAddress,
        creditCardInformation: {
          accountHolder: request.creditCardInformation.accountHolder,
          cardNumber: request.creditCardInformation.cardNumber.replace(
            / /g,
            ""
          ),
          cvc: request.creditCardInformation.cvc,
          month: monthYear[0],
          year: "20" + monthYear[1],
          postalCode: request.creditCardInformation.postalCode,
        },
      };
    } else {
      if (
        request.bankAccountInformation.confirmAccountNumber !==
        request.bankAccountInformation.accountNumber
      ) {
        this.setState({
          submitting: false,
          errorMessage: "Account Number does not match.",
        });
        return;
      }
      tokenRequest = {
        payer: request.payer,
        emailAddress: request.emailAddress,
        bankAccountInformation: {
          accountHolder:
            request.bankAccountInformation.accountHolder === ""
              ? // tslint:disable-next-line:prefer-template
              request.bankAccountInformation.firstName! +
              " " +
              request.bankAccountInformation.lastName!
              : request.bankAccountInformation.accountHolder,
          firstName: request.bankAccountInformation.firstName,
          lastName: request.bankAccountInformation.lastName,
          accountType: request.bankAccountInformation.accountType,
          routingNumber: request.bankAccountInformation.routingNumber,
          accountNumber: request.bankAccountInformation.accountNumber,
        },
      };
    }


    /**
     * seperate invoices id by comma and send to backend rather than doing all these in backend
     */

    const validationResult = this.state.paymentValidationResult;
    const selectedInvoices = this.getSelectedInvoices();
    const invoiceNumbers = validationResult?.invoices?.map((invoice) => invoice.invoiceNumber)
      ?? selectedInvoices.map((invoice) => invoice.InvoiceNumber);

    const invoicePaymentsPayload = validationResult?.invoices?.map((invoice) => ({
      invoiceNumber: invoice.invoiceNumber,
      amount: invoice.requestedAmount,
    })) ?? selectedInvoices.map((invoice) => ({
      invoiceNumber: invoice.InvoiceNumber,
      amount: parseFloat(invoice.amount),
    }));

    const scheduledDateValue = this.state.isScheduled ? this.getScheduledDateString() : undefined;

    const tokenDetails = JSON.stringify(tokenRequest);
    await backendAPI
      .getEpayToken(JSON.parse(tokenDetails), this.props.impersonationToken)
      .then(async (response) => {
        const epayToken = response.headers.location.split("/")[2];

        const paymentRequest = {
          companyName: this.props.companyName,
          payer: request.payer,
          payerFee: request.payerFee,
          emailAddress: request.emailAddress,
          // ePay's `amount` parameter is interpreted differently per endpoint:
          //  - /transactions/   amount = gross (what payer is charged). ePay
          //                     deducts payerFee from amount to compute net.
          //                     Send amount = invoice + fee.
          //  - /paymentSchedules/  amount = net (merchant amount). ePay ADDS
          //                     payerFee on top when executing the charge.
          //                     Send amount = bare invoice.
          // Verified Apr 28 against ePay UI for IDAHO FOREST schedule (post
          // executes as amount + payerFee) and ROADSHOW transaction (post is
          // amount itself). Conflating them caused Tina's Apr 24 over-net and
          // Apr 27 under-net complaints respectively.
          amount: this.state.isScheduled ? this.state.amount : this.state.total,
          epayToken: epayToken,
          isScheduled: this.state.isScheduled,
          scheduledDate: scheduledDateValue,
          companyImpersonationToken: this.props.impersonationToken,
          policyNumber: this.props.policyNumber,
          insuredName: this.state.insuredName,
          paymentComment: this.state.paymentComment,
          invoicePayments: invoicePaymentsPayload,
          invoiceNumbers: invoiceNumbers,
          invoices_id: invoiceNumbers,
          payorState: this.state.payorState, // Send state ID to backend
          paymentMethod: this.state.paymentMethod,

        };
        console.log(paymentRequest);
        const paymentDetails = JSON.stringify(paymentRequest);
        await backendAPI
          .sendPayment(JSON.parse(paymentDetails))
          .then(() => {
            console.log(paymentDetails)
            // this.setState({ submitting: false });
            this.setState({
              submitting: false,
              redirect: {
                pathname: "payment-received",
                state: { isScheduled: this.state.isScheduled },
              }
            });
          })
          .catch((error) => {
            this.setErrorMessage(error);
          });
      })
      .catch((error) => {
        this.setErrorMessage(error);
      });
  }

  setErrorMessage(error: any) {
    if (!error.response) {
      this.setState({
        submitting: false,
        errorMessage:
          "Error connecting to payment server. Please try again later.",
      });
      return;
    } else if (error.response.status === 500) {
      this.setState({
        submitting: false,
        errorMessage: "Internal Server Error",
      });
      return;
    }
    if (error.response.data.modelState) {
      const errors = Object.values(error.response.data.modelState);
      const errorString = errors.map((err: any) => err[0]);
      this.setState({
        submitting: false,
        errorMessage: errorString.toString().split(",").join("\r"),
      });
    } else if (error.response.data.message) {
      this.setState({
        submitting: false,
        errorMessage: error.response.data.message,
      });
    } else {
      this.setState({
        submitting: false,
        errorMessage: "Failed to make payment. Please try again later.",
      });
    }
  }

  handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await this.setState({
      creditCardInformation: {
        ...this.state.creditCardInformation,
        cardNumber: this.state.creditCardInformation.cardNumber, // Todo: trim out the whitespace somehow
      },
    });
    // console.log(this.state);
    await this.sendPayment(this.state);
  };

  getPaymentDetailHtml = () => (
    <React.Fragment>
      <tr>
        <td>
          Policy Number:{" "}
          {this.props.policyNumber ? this.props.policyNumber : "N/A"}
        </td>
      </tr>

      <tr>
        <td>Amount:</td>
        <td className="text-right">
          {numberFormatter.format(this.state.amount ? this.state.amount : 0)}
        </td>
      </tr>
      {this.state.paymentMethod === "cc" ? (
        <tr>
          <td>ePay Convenience Fee (3.75%):</td>
          <td className="text-right">
            {numberFormatter.format(this.state.payerFee)}
          </td>
        </tr>
      ) : (
        <tr>
          <td>ePay Convenience Fee ($3.00):</td>
          <td className="text-right">
            {numberFormatter.format(this.state.payerFee)}
          </td>
        </tr>
      )}
      <tr className="border-top mt-2">
        <td>Total:</td>
        <td className="text-right">
          {numberFormatter.format(this.state.total)}
        </td>
      </tr>
    </React.Fragment>
  );

  handleTosAccepted() {
    this.setState({ tosAccepted: !this.state.tosAccepted });
  }

  handleScheduleAccepted() {
    if (!this.isSchedulingAvailable(this.state.paymentValidationResult)) {
      return;
    }

    const nextScheduleAccepted = !this.state.scheduleAccepted;
    const updates: Partial<PaymentFormState> = {
      scheduleAccepted: nextScheduleAccepted,
      isScheduled: nextScheduleAccepted,
    };

    if (!nextScheduleAccepted) {
      updates.scheduledDate = datetoObject(new Date(Date.now() + 86400000).toString());
    }

    this.setState(updates as any, () => {
      this.runValidation();
    });
  }

  handleCCClick = () => {
    if (STATES_WITHOUT_CC_SURCHARGE.includes(this.state.payorState)) {
      this.setState({ showCCAlert: true });
      setTimeout(() => this.setState({ showCCAlert: false }), 5000); // Hide alert after 5 seconds
    }
  };

  // Helper function to get state abbreviation from state ID
  getStateAbbreviationFromId = (stateId: string): string => {
    const state = US_STATES.find(s => s.id === stateId);
    return state ? state.abbreviation : '';
  };

  renderPaymentForm() {

    // console.table(this.props);

    const validationResult = this.state.paymentValidationResult;
    const stateAbbreviation = this.getStateAbbreviationFromId(this.state.payorState);
    const isCCDisabled = STATES_WITHOUT_CC_SURCHARGE.includes(stateAbbreviation);
    const scheduleWindow = validationResult?.scheduleWindow;
    const schedulingAllowed = this.isSchedulingAvailable(validationResult);
    const tomorrowObj = datetoObject(new Date(Date.now() + 86400000).toString());
    const minimumScheduleDate = schedulingAllowed && scheduleWindow?.min ? datetoObject(scheduleWindow.min) : tomorrowObj;
    const maximumScheduleDate = schedulingAllowed && scheduleWindow?.max
      ? datetoObject(scheduleWindow.max)
      : datetoObject(formatDrewDate(this.props.dueDate));

    return (
      <Form noValidate={true} onSubmit={this.handleSubmit}>
        <Row>
          <Col lg={8} className="pr-4">
            {/* Payment Type Validation */}
            <PaymentTypeValidator
              validationResult={validationResult}
              isLoading={this.state.validationLoading}
              invoicePayments={this.state.invoicePayments}
              onAmountChange={this.handleInvoiceAmountChange}
            />

            <BasicInfoFields
              payee={this.state.payer}
              amount={this.state.amount!}
              policyNumber={this.props.policyNumber}
              handleInputChange={this.handleInputChange}
              isPaymentPlan={this.props.isPaymentPlan}
              paymentComment={this.state.paymentComment}
              payorState={this.state.payorState}
            />
            <hr />
            <Row>
              <Col>
                <h2 className="text-center" style={{ margin: "0" }}>
                  Choose Payment Type
                </h2>
              </Col>
            </Row>
            <Row style={{ marginTop: "10px" }}>
              <Col className="text-center">
                {this.state.showCCAlert && (
                  <Alert variant="warning" onClose={() => this.setState({ showCCAlert: false })} dismissible={false}>
                    Credit card payments are not available for {US_STATES.find(state => state.abbreviation === this.state.payorState)?.name} due to state regulations.
                  </Alert>
                )}
                <ToggleButtonGroup
              name="payment-method-toggle"
              type="radio"
              defaultValue={this.state.paymentMethod}
              onChange={this.togglePaymentMethod}
              className="ios_tab"

            >
              <ToggleButton
                type="radio"
                value="cc"
                style={{ cursor: isCCDisabled ? "not-allowed" : "pointer" }}
                id="cc_radio"
                disabled={isCCDisabled}
                onClick={this.handleCCClick}
              >
                Credit Card
              </ToggleButton>
              <ToggleButton
                type="radio"
                value="ach"
                style={{ cursor: "pointer" }}
                id="ach_radio"

              >
                ACH
              </ToggleButton>
            </ToggleButtonGroup>
          </Col>
        </Row>
        <hr />
        {this.state.paymentMethod === "cc" ? (
          <CreditCardFields
            creditCardInformation={this.state.creditCardInformation}
            issuer={this.state.issuer}
            focused={this.state.focused}
            handleCallback={this.handleCallback}
            handleInputFocus={this.handleInputFocus}
            handleInputChange={this.handleInputChange}
          />
        ) : (
          <ACHFields
            bankAccountInformation={this.state.bankAccountInformation}
            toggleAchType={this.toggleAchType}
            handleInputChange={this.handleInputChange}
            bankName={this.state.bankName}
          />
        )}
      </Col>
      <Col lg={4} className="border-left pl-lg-4">
        <Row>
          <Col>
            <h2 className="text-center">Payment Details</h2>
            <table className="w-100">
              <tbody>{this.getPaymentDetailHtml()}</tbody>
            </table>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={styles["terms-of-service"]}>
              <br />
              I understand that submitting payment does not
              guarantee coverage until confirmed by your agent or the
              insurance company.
              <br />
              <br />
              Additionally, I understand that payment does not
              guarantee coverage in the event the policy has entered
              a cancellation status by the insurance company. Payment
              posting can take 2 to 3 business days. 
              <br />
              <br />I also agree to the charge of the ePay Convenience Fee
              which will appear as a separate transaction from
              ePayPolicy on your bank or credit card statement.
            </div>
          </Col>
        </Row>
        <Col xs={12} sm={true}>
          <Form.Check style={{ marginTop: "16px" }}>
            <Form.Check.Input onClick={() => this.handleTosAccepted()} />
            <Form.Check.Label>I agree to the terms above</Form.Check.Label>
          </Form.Check>
        </Col>
        <Row>
          <Col>
            {!this.state.submitting ? (
              <Button
                variant="primary"
                type="submit"
                className="mt-3"
                disabled={
                  !this.state.tosAccepted
                  || (this.state.paymentValidationResult && !this.state.paymentValidationResult.isValid)
                  || (!this.props.policyNumber && !(this.state.insuredName ?? '').trim())
                }
              >
                {(this.state.isScheduled) ? "SCHEDULE PAYMENT" : "MAKE PAYMENT"}
              </Button>
            ) : (
              <Spinner animation={"border"} className="mt-3" />
            )}
          </Col>
        </Row>

        {schedulingAllowed && (
          <Col xs={12} sm={true}
            className="schedule-payment-wrapper" style={{ marginTop: "24px" }}>

            <Form.Check style={{ marginLeft: "30px" }}>
              <Form.Check.Input onClick={() => this.handleScheduleAccepted()} checked={this.state.scheduleAccepted} readOnly />
              <Form.Check.Label>Schedule future payment date</Form.Check.Label>
            </Form.Check>

            {this.state.scheduleAccepted && (
              <DatePicker
                value={this.state.scheduledDate}
                onChange={(date) => this.setState({ scheduledDate: date, isScheduled: true }, () => this.runValidation())}
                minimumDate={minimumScheduleDate}
                maximumDate={maximumScheduleDate}
                inputPlaceholder="Select a day"
                shouldHighlightWeekends
                colorPrimary="#1313b7"
              />)
            }
            {scheduleWindow?.min && scheduleWindow?.max && (
              <small className="text-muted" style={{ marginLeft: "30px" }}>
                Schedule between {scheduleWindow.min} and {scheduleWindow.max}.
              </small>
            )}
          </Col>
        )}
        <Row className="float-right">
          {this.state.errorMessage ? (
            <p
              style={{
                color: "red",
                paddingTop: "1rem",
                marginRight: "1rem",
              }}
            >
              {this.state.errorMessage}
            </p>
          ) : null}
        </Row>
      </Col>
    </Row>
  </Form>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.state.redirect ? React.createElement(Redirect as any, { to: this.state.redirect }) : null}
        {this.renderPaymentForm()}
      </React.Fragment>
    );
  }
}

export default MasterPaymentForm;
